import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import type { DashboardDateRange } from '@/components/dashboard/model';
import {
  dashboardTimeline,
  dashboardMetricValuesSchema,
  dashboardTimelinePointSchema,
  type DashboardMetricValues,
} from '../../../dashboard/model';

const optionalNumber = nullableOptional(z.number());
const optionalId = nullableOptional(z.union([z.string(), z.number()]).transform(String));

const networkPerformanceBaseSchema = dashboardMetricValuesSchema.extend({
  adRequestsFilled: optionalNumber,
});

export const networkPlacementPerformanceSchema = networkPerformanceBaseSchema.extend({
  placementId: optionalId,
  placementName: nullableOptional(z.string()),
  placement: nullableOptional(z.string()),
  name: nullableOptional(z.string()),
  global7daysRevenuePct: optionalNumber,
  revenuePct: optionalNumber,
});

export const networkAppPerformanceSchema = networkPerformanceBaseSchema.extend({
  appId: optionalId,
  appName: nullableOptional(z.string()),
  name: nullableOptional(z.string()),
});

export const networkDomainPerformanceSchema = networkPerformanceBaseSchema.extend({
  domainId: optionalId,
  domain: nullableOptional(z.string()),
  name: nullableOptional(z.string()),
});

export const networkDashboardOverviewSchema = z.looseObject({
  previousPeriodEnd: z.iso.datetime({ offset: true }).nullish(),
  total: dashboardMetricValuesSchema
    .nullish()
    .transform((value): DashboardMetricValues => value ?? {}),
  totalPrevious: dashboardMetricValuesSchema
    .nullish()
    .transform((value): DashboardMetricValues | null => value ?? null),
  hours: nullableArray(dashboardTimelinePointSchema),
  totalsByDay: nullableArray(dashboardTimelinePointSchema),
  placements: nullableArray(networkPlacementPerformanceSchema),
  apps: nullableArray(networkAppPerformanceSchema),
  domains: nullableArray(networkDomainPerformanceSchema),
});

export const networkDemandSourceRowSchema = dashboardMetricValuesSchema.extend({
  demandPartnerId: optionalId,
  demandPartnerName: nullableOptional(z.string()),
  demandPartner: nullableOptional(z.string()),
  dpAdRequests: optionalNumber,
  wonBids: optionalNumber,
  bidFillRatio: optionalNumber,
  bidRatio: optionalNumber,
});

export const networkDemandSourcesSchema = z.looseObject({
  total: dashboardMetricValuesSchema
    .extend({ wonBids: optionalNumber })
    .nullish()
    .transform((value) => value ?? {}),
  report: z.preprocess(
    (value) => (value === null ? [] : value),
    z.array(networkDemandSourceRowSchema),
  ),
});

export type NetworkDashboardOverview = z.infer<typeof networkDashboardOverviewSchema>;
export type NetworkPlacementPerformance = z.infer<typeof networkPlacementPerformanceSchema>;
export type NetworkAppPerformance = z.infer<typeof networkAppPerformanceSchema>;
export type NetworkDomainPerformance = z.infer<typeof networkDomainPerformanceSchema>;
export type NetworkDemandSources = z.infer<typeof networkDemandSourcesSchema>;
export type NetworkDemandSourceRow = z.infer<typeof networkDemandSourceRowSchema>;

export function networkDashboardTimeline(
  overview: NetworkDashboardOverview,
  range: DashboardDateRange,
) {
  return dashboardTimeline(overview, range);
}

export function networkPerformanceName(
  item: NetworkPlacementPerformance | NetworkAppPerformance | NetworkDomainPerformance,
) {
  return (
    ('placementName' in item ? readString(item.placementName) : undefined) ??
    ('placement' in item ? readString(item.placement) : undefined) ??
    ('appName' in item ? readString(item.appName) : undefined) ??
    ('domain' in item ? readString(item.domain) : undefined) ??
    readString(item.name) ??
    '-'
  );
}

export function networkPerformanceId(
  item: NetworkPlacementPerformance | NetworkAppPerformance | NetworkDomainPerformance,
) {
  return (
    ('placementId' in item ? readString(item.placementId) : undefined) ??
    ('appId' in item ? readString(item.appId) : undefined) ??
    ('domainId' in item ? readString(item.domainId) : undefined) ??
    networkPerformanceName(item)
  );
}

export function networkPlacementRevenuePercentage(
  item: NetworkPlacementPerformance | NetworkAppPerformance | NetworkDomainPerformance,
) {
  return (
    ('global7daysRevenuePct' in item ? readNumber(item.global7daysRevenuePct) : undefined) ??
    ('revenuePct' in item ? readNumber(item.revenuePct) : undefined)
  );
}

function readString(value: unknown) {
  return typeof value === 'string' ? value : undefined;
}

function readNumber(value: unknown) {
  return typeof value === 'number' ? value : undefined;
}
